import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getAuthHeaders } from '../lib/auth';
import { Button } from '../components/ui/button';
import { ArrowLeft, Copy, Check, ChevronDown, ChevronUp, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const PAID_TIERS = [
  'founder_monthly', 'pastor_pro', 'founder_lifetime', 'seminary_annual',
  'ministry_plus', 'church_team', 'pro',
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTENT_TYPES = [
  {
    key: 'guide',
    label: 'Home Group Guide',
    desc: 'A full small group discussion guide with questions and reflection.',
    borderColor: 'border-teal-400',
    bgColor: 'bg-teal-50',
    accentColor: 'text-teal-700',
    activeBg: 'bg-teal-50 border-teal-400',
    icon: '📚',
  },
  {
    key: 'devotional',
    label: '6-Day Devotional',
    desc: 'A week-long devotional series drawn from the sermon.',
    borderColor: 'border-amber-400',
    bgColor: 'bg-amber-50',
    accentColor: 'text-amber-700',
    activeBg: 'bg-amber-50 border-amber-400',
    icon: '📖',
  },
  {
    key: 'social',
    label: 'Social Posts',
    desc: '20 ready-to-share quotes and posts for X/Twitter.',
    borderColor: 'border-sky-400',
    bgColor: 'bg-sky-50',
    accentColor: 'text-sky-700',
    activeBg: 'bg-sky-50 border-sky-400',
    icon: '🐦',
  },
  {
    key: 'ebook',
    label: 'Sermon eBook',
    desc: 'Long-form written content drawn from your message.',
    borderColor: 'border-violet-400',
    bgColor: 'bg-violet-50',
    accentColor: 'text-violet-700',
    activeBg: 'bg-violet-50 border-violet-400',
    icon: '📝',
  },
  {
    key: 'newsletter',
    label: 'Email Newsletter',
    desc: '500–750 word pastoral email ready to send to your congregation.',
    borderColor: 'border-emerald-400',
    bgColor: 'bg-emerald-50',
    accentColor: 'text-emerald-700',
    activeBg: 'bg-emerald-50 border-emerald-400',
    icon: '✉️',
  },
  {
    key: 'study_notes',
    label: 'Study Notes',
    desc: 'Comprehensive theological study notes with all scripture references, themes, and discussion questions.',
    borderColor: 'border-orange-400',
    bgColor: 'bg-orange-50',
    accentColor: 'text-orange-700',
    activeBg: 'bg-orange-50 border-orange-400',
    icon: '📋',
  },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 transition-colors px-2 py-1 rounded border border-stone-200 hover:border-stone-300 bg-white"
    >
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function ResultSection({ type, data }) {
  const [open, setOpen] = useState(true);
  const config = CONTENT_TYPES.find((c) => c.key === type);

  if (!data) return null;
  if (data.error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p className="text-sm font-semibold text-red-700">{config?.label}: generation failed</p>
        <p className="text-xs text-red-600 mt-1">{data.error}</p>
      </div>
    );
  }

  const renderContent = () => {
    if (type === 'guide') {
      const sections = data.sections || [];
      return (
        <div className="space-y-4">
          {data.leader_note && (
            <div className="bg-teal-50 border border-teal-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-1">Leader Note</p>
              <p className="text-sm text-teal-800 leading-relaxed">{data.leader_note}</p>
            </div>
          )}
          {sections.map((section, i) => (
            <div key={i} className="border border-stone-100 rounded-lg p-4 bg-white">
              <p className="font-semibold text-stone-800 mb-2">{section.heading}</p>
              {section.questions && section.questions.length > 0 && (
                <ol className="list-decimal list-inside space-y-1 mb-3">
                  {section.questions.map((q, qi) => (
                    <li key={qi} className="text-sm text-stone-700">{q}</li>
                  ))}
                </ol>
              )}
              {section.reflection && (
                <p className="text-xs text-stone-500 italic border-t border-stone-100 pt-2 mt-2">{section.reflection}</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'devotional') {
      const days = data.days || data.dailyWords || [];
      return (
        <div className="space-y-3">
          {days.map((day, i) => (
            <div key={i} className="border border-stone-100 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <p className="font-semibold text-stone-800">
                  {day.day_name || day.day || `Day ${i + 1}`}{day.title ? ` — ${day.title}` : ''}
                </p>
                <span className="text-xs text-amber-600 font-medium">{day.scripture}</span>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">{day.reflection || day.devotional}</p>
              {day.prayer && (
                <p className="text-xs text-stone-500 italic border-t border-stone-100 pt-2">{day.prayer}</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (type === 'social') {
      const posts = data.posts || data.tweets || [];
      const allText = posts.join('\n\n---\n\n');
      return (
        <div className="space-y-2">
          <div className="flex justify-end mb-2">
            <CopyButton text={allText} />
          </div>
          {posts.map((post, i) => (
            <div key={i} className="flex items-start gap-3 border border-stone-100 rounded-lg p-3 bg-white">
              <span className="text-xs text-stone-400 font-mono w-6 flex-shrink-0 mt-0.5">{i + 1}.</span>
              <p className="text-sm text-stone-700 flex-1 leading-relaxed">{post}</p>
              <CopyButton text={post} />
            </div>
          ))}
        </div>
      );
    }

    if (type === 'ebook') {
      return (
        <div className="bg-white rounded-lg border border-stone-100 p-5">
          {data.title && (
            <h3 className="font-serif font-bold text-xl text-[#1e3a8a] mb-1">{data.title}</h3>
          )}
          {data.subtitle && (
            <p className="text-sm text-stone-500 italic mb-4">{data.subtitle}</p>
          )}
          {data.content && (
            <div className="prose prose-sm max-w-none text-stone-700 whitespace-pre-wrap leading-relaxed">
              {data.content}
            </div>
          )}
          {!data.content && !data.title && (
            <pre className="text-xs text-stone-600 whitespace-pre-wrap overflow-auto">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </div>
      );
    }

    if (type === 'newsletter') {
      const paragraphs = data.paragraphs || (data.body ? data.body.split('\n\n').filter(Boolean) : []);
      const fullText = [data.subject && `Subject: ${data.subject}`, ...paragraphs].filter(Boolean).join('\n\n');
      return (
        <div className="bg-white rounded-lg border border-stone-100 p-5 space-y-4">
          {data.subject && (
            <div className="pb-4 border-b border-stone-100">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1">Subject Line</p>
              <p className="font-semibold text-stone-800">{data.subject}</p>
              {data.preview_text && (
                <p className="text-xs text-stone-400 mt-1 italic">{data.preview_text}</p>
              )}
            </div>
          )}
          {paragraphs.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Email Body</p>
                <CopyButton text={fullText} />
              </div>
              <div className="space-y-4">
                {paragraphs.map((para, i) => (
                  <p key={i} className="text-sm text-stone-700 leading-relaxed">{para}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (type === 'study_notes') {
      return (
        <div className="space-y-4">
          {data.overview && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-2">Overview</p>
              <p className="text-sm text-stone-700 leading-relaxed">{data.overview}</p>
              {data.main_theme && (
                <p className="text-xs text-stone-500 mt-2 italic">Theme: {data.main_theme}</p>
              )}
              {data.key_scripture && (
                <p className="text-xs font-semibold text-orange-700 mt-1">Key Passage: {data.key_scripture}</p>
              )}
            </div>
          )}

          {data.scripture_references?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Scripture References</p>
              <div className="space-y-3">
                {data.scripture_references.map((s, i) => (
                  <div key={i} className="border-l-4 border-orange-200 pl-3">
                    <p className="font-semibold text-sm text-stone-800">{s.reference}</p>
                    <p className="text-xs text-stone-600 mt-0.5">{s.context}</p>
                    {s.significance && <p className="text-xs text-stone-400 mt-0.5 italic">{s.significance}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.theological_themes?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Theological Themes</p>
              <div className="space-y-3">
                {data.theological_themes.map((t, i) => (
                  <div key={i}>
                    <p className="font-semibold text-sm text-stone-800">{t.theme}</p>
                    <p className="text-xs text-stone-600 mt-0.5 leading-relaxed">{t.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.sermon_outline?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Sermon Outline</p>
              <div className="space-y-3">
                {data.sermon_outline.map((point, i) => (
                  <div key={i}>
                    <p className="font-semibold text-sm text-stone-800">{i + 1}. {point.point}</p>
                    {point.supporting_scripture && (
                      <p className="text-xs text-orange-600 font-medium mt-0.5">{point.supporting_scripture}</p>
                    )}
                    {point.sub_points?.map((sp, si) => (
                      <p key={si} className="text-xs text-stone-600 ml-4 mt-0.5">• {sp}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.key_quotes?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Key Quotes</p>
              <div className="space-y-2">
                {data.key_quotes.map((q, i) => (
                  <blockquote key={i} className="border-l-4 border-amber-300 pl-3 py-1">
                    <p className="text-sm text-stone-700 italic">"{q}"</p>
                  </blockquote>
                ))}
              </div>
            </div>
          )}

          {data.application_points?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Application Points</p>
              <ul className="space-y-2">
                {data.application_points.map((ap, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                    <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    {ap}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.discussion_questions?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Discussion Questions</p>
              <ol className="space-y-2 list-decimal list-inside">
                {data.discussion_questions.map((q, i) => (
                  <li key={i} className="text-sm text-stone-700">{q}</li>
                ))}
              </ol>
            </div>
          )}

          {data.further_study?.length > 0 && (
            <div className="bg-white border border-stone-100 rounded-lg p-4">
              <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Further Study</p>
              <ul className="space-y-1">
                {data.further_study.map((item, i) => (
                  <li key={i} className="text-sm text-stone-600 flex items-start gap-2">
                    <span className="text-orange-400 flex-shrink-0">→</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }

    // Fallback
    return (
      <pre className="text-xs text-stone-600 whitespace-pre-wrap overflow-auto bg-white border border-stone-100 rounded-lg p-4">
        {JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  return (
    <div className={`rounded-xl border-2 ${config?.borderColor || 'border-stone-200'} overflow-hidden`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between p-4 ${config?.bgColor || 'bg-stone-50'} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          <span className="text-xl">{config?.icon}</span>
          <span className={`font-semibold ${config?.accentColor || 'text-stone-700'}`}>{config?.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {open && <CopyButton text={typeof data === 'string' ? data : JSON.stringify(data, null, 2)} />}
          {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
        </div>
      </button>
      {open && (
        <div className="p-4 bg-stone-50/50">
          {renderContent()}
        </div>
      )}
    </div>
  );
}

export default function CreatePage() {
  const { analysisId } = useParams();
  const navigate = useNavigate();

  const [sermon, setSermon] = useState(null);
  const [isLoadingSermon, setIsLoadingSermon] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [usage, setUsage] = useState(null);
  const [tier, setTier] = useState(null);

  useEffect(() => {
    const headers = getAuthHeaders();
    if (!headers || Object.keys(headers).length === 0) {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const [sermonRes, usageRes] = await Promise.all([
          fetch(`${API}/sermons/${analysisId}`, { headers }),
          fetch(`${API}/usage`, { headers }),
        ]);

        if (sermonRes.status === 401) { navigate('/login'); return; }

        if (sermonRes.ok) {
          const data = await sermonRes.json();
          setSermon(data);
          setVideoTitle(data.video_title || 'Your Sermon');
        } else {
          toast.error('Could not load sermon. It may have been deleted.');
          navigate('/dashboard');
          return;
        }

        if (usageRes.ok) {
          const usageData = await usageRes.json();
          setUsage(usageData.usage?.sermon_analysis);
          setTier(usageData.tier);
        }
      } catch {
        toast.error('Something went wrong loading the sermon.');
        navigate('/dashboard');
      } finally {
        setIsLoadingSermon(false);
      }
    };

    fetchData();
  }, [analysisId, navigate]);

  const toggleType = (key) => {
    setSelectedTypes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleGenerate = async () => {
    if (selectedTypes.length === 0) return;
    setIsGenerating(true);
    setResults(null);

    try {
      const response = await fetch(`${API}/generate-from-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ analysis_id: analysisId, content_types: selectedTypes }),
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data.results);
        setVideoTitle(data.video_title || videoTitle);
        toast.success('Content generated successfully!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Generation failed. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoadingSermon) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Back link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#1e3a8a] transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Upgrade banner for free users */}
        {tier && !PAID_TIERS.includes(tier) && usage && !usage.unlimited && (
          <div className={`mb-6 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
            usage.remaining === 0
              ? 'bg-red-50 border border-red-200'
              : usage.remaining === 1
              ? 'bg-amber-50 border border-amber-200'
              : 'bg-[#1e3a8a]/5 border border-[#1e3a8a]/20'
          }`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Crown className={`w-4 h-4 flex-shrink-0 ${
                  usage.remaining === 0 ? 'text-red-500' : usage.remaining === 1 ? 'text-amber-500' : 'text-[#1e3a8a]'
                }`} />
                <p className={`font-semibold text-sm ${
                  usage.remaining === 0 ? 'text-red-800' : usage.remaining === 1 ? 'text-amber-800' : 'text-[#1e3a8a]'
                }`}>
                  {usage.remaining === 0
                    ? 'You\'ve used all your free sifts this month.'
                    : usage.remaining === 1
                    ? 'This was your last free sift this month.'
                    : `${usage.remaining - 1} free sift${usage.remaining - 1 !== 1 ? 's' : ''} remaining after this one.`}
                </p>
              </div>
              <p className={`text-xs leading-relaxed ${
                usage.remaining === 0 ? 'text-red-700' : usage.remaining === 1 ? 'text-amber-700' : 'text-[#1e3a8a]/70'
              }`}>
                {usage.remaining === 0
                  ? 'Upgrade to Pastor Pro for unlimited sermon sifts, all content tools, and no monthly limits.'
                  : 'Upgrade to Pastor Pro for unlimited sifts and access to every content tool — no limits, ever.'}
              </p>
            </div>
            <Link
              to="/pricing"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                usage.remaining === 0
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : usage.remaining === 1
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Upgrade to Pastor Pro
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-bold text-[#1e3a8a] mb-2 leading-tight">
            Create Content
          </h1>
          <p className="text-stone-500 text-sm">
            From: <span className="font-medium text-stone-700">{videoTitle}</span>
          </p>
        </div>

        {/* Content type selection */}
        {!results && (
          <>
            <p className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">
              Select what to generate
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {CONTENT_TYPES.map((ct) => {
                const selected = selectedTypes.includes(ct.key);
                return (
                  <button
                    key={ct.key}
                    onClick={() => toggleType(ct.key)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      selected
                        ? `${ct.activeBg} ${ct.borderColor}`
                        : 'bg-white border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <span className="text-2xl flex-shrink-0">{ct.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm ${selected ? ct.accentColor : 'text-stone-700'}`}>
                        {ct.label}
                      </p>
                      <p className="text-xs text-stone-500 mt-0.5 leading-snug">{ct.desc}</p>
                    </div>
                    {selected && (
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${ct.accentColor}`} />
                    )}
                  </button>
                );
              })}
            </div>

            <Button
              onClick={handleGenerate}
              disabled={selectedTypes.length === 0 || isGenerating}
              className="w-full btn-primary"
            >
              {isGenerating ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating your content...
                </span>
              ) : (
                `Generate ${selectedTypes.length} Item${selectedTypes.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </>
        )}

        {/* Loading state */}
        {isGenerating && (
          <div className="mt-8 text-center">
            <div className="w-10 h-10 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-stone-600 font-medium">Generating your content...</p>
            <p className="text-stone-400 text-sm mt-1">This may take 30–60 seconds depending on selections.</p>
          </div>
        )}

        {/* Results */}
        {results && !isGenerating && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl font-bold text-[#1e3a8a]">Your Content</h2>
              <button
                onClick={() => { setResults(null); setSelectedTypes([]); }}
                className="text-xs text-stone-400 hover:text-stone-600 underline"
              >
                Generate more
              </button>
            </div>
            <div className="space-y-4">
              {selectedTypes.map((type) => (
                <ResultSection key={type} type={type} data={results[type]} />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
